/**
 * Organization Support Tools Tab
 *
 * Enter support mode, create jobs, run checks as org.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Shield,
  Plus,
  Play,
  Download,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Building2,
} from 'lucide-react';
import { Org } from '@/integrations/firebase/types';

interface OrgSupportTabProps {
  org: Org;
}

export function OrgSupportTab({ org }: OrgSupportTabProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canImpersonate, canRunChecks } = useAdminAuth();
  const {
    isInSupportMode,
    actingOrgId,
    enterSupportMode,
    writeActionsEnabled,
  } = useSupportMode();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'impersonate' | 'create_job' | 'run_check'>('impersonate');
  const [loading, setLoading] = useState(false);

  // Job creation form (simplified)
  const [jobForm, setJobForm] = useState({
    title: '',
    projectType: 'residential',
    ahj: '',
    tradeScope: '',
  });

  const isActingAsThisOrg = isInSupportMode && actingOrgId === org.id;

  const handleEnterSupportMode = async () => {
    setLoading(true);
    const result = await enterSupportMode(org.id);
    setLoading(false);

    if (result.success) {
      toast({
        title: 'Support Mode Activated',
        description: `Now acting as ${org.name || org.id}`,
      });
    } else {
      toast({
        title: 'Failed',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const handleCreateJob = async () => {
    if (!writeActionsEnabled) {
      toast({
        title: 'Write Actions Disabled',
        description: 'Enable write actions in the support mode banner to create jobs',
        variant: 'destructive',
      });
      return;
    }

    // Placeholder - would create job via cloud function
    toast({
      title: 'Not Implemented',
      description: 'Job creation will be wired to backend',
    });
  };

  const handleRunCheck = async () => {
    // Placeholder - would run check via cloud function
    toast({
      title: 'Not Implemented',
      description: 'Check execution will be wired to backend',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-white mb-1">Support Tools</h2>
        <p className="text-sm text-gray-400">
          Act as this organization to troubleshoot issues or run compliance checks
        </p>
      </div>

      {/* Support Mode Status */}
      <AdminCard className={isActingAsThisOrg ? 'border-amber-500/50' : ''}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                isActingAsThisOrg ? 'bg-amber-500/20' : 'bg-[hsl(220,15%,15%)]'
              }`}
            >
              <Shield
                className={`h-5 w-5 ${
                  isActingAsThisOrg ? 'text-amber-400' : 'text-gray-400'
                }`}
              />
            </div>
            <div>
              <p className="font-medium text-white">Support Mode</p>
              <p className="text-sm text-gray-400">
                {isActingAsThisOrg
                  ? 'Currently active for this organization'
                  : 'Enter to act as this organization'}
              </p>
            </div>
          </div>

          {isActingAsThisOrg ? (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40">
              <CheckCircle className="h-3 w-3 mr-1" />
              Active
            </Badge>
          ) : (
            <Button
              onClick={handleEnterSupportMode}
              disabled={!canImpersonate || loading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Shield className="h-4 w-4 mr-2" />
              )}
              Enter Support Mode
            </Button>
          )}
        </div>

        {isActingAsThisOrg && (
          <div className="mt-4 pt-4 border-t border-[hsl(220,15%,20%)]">
            <div className="flex items-center gap-2 text-sm">
              <div
                className={`h-2 w-2 rounded-full ${
                  writeActionsEnabled ? 'bg-green-500' : 'bg-gray-500'
                }`}
              />
              <span className={writeActionsEnabled ? 'text-green-400' : 'text-gray-400'}>
                Write actions {writeActionsEnabled ? 'enabled' : 'disabled'}
              </span>
            </div>
          </div>
        )}
      </AdminCard>

      {/* Create Job */}
      <AdminCard>
        <div className="flex items-center gap-2 mb-4">
          <Plus className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-white">Create Job</h3>
          {!isActingAsThisOrg && (
            <Badge variant="outline" className="text-xs border-gray-600 text-gray-500">
              Requires Support Mode
            </Badge>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Job Title</Label>
              <Input
                value={jobForm.title}
                onChange={(e) => setJobForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g., HVAC Replacement - 123 Main St"
                disabled={!isActingAsThisOrg}
                className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Project Type</Label>
              <Select
                value={jobForm.projectType}
                onValueChange={(v) => setJobForm((p) => ({ ...p, projectType: v }))}
                disabled={!isActingAsThisOrg}
              >
                <SelectTrigger className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-300">AHJ</Label>
              <Select
                value={jobForm.ahj}
                onValueChange={(v) => setJobForm((p) => ({ ...p, ahj: v }))}
                disabled={!isActingAsThisOrg}
              >
                <SelectTrigger className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
                  <SelectValue placeholder="Select jurisdiction" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
                  <SelectItem value="jacksonville-coj">Jacksonville (COJ)</SelectItem>
                  <SelectItem value="miami-dade">Miami-Dade County</SelectItem>
                  <SelectItem value="broward">Broward County</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Trade Scope</Label>
              <Select
                value={jobForm.tradeScope}
                onValueChange={(v) => setJobForm((p) => ({ ...p, tradeScope: v }))}
                disabled={!isActingAsThisOrg}
              >
                <SelectTrigger className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
                  <SelectValue placeholder="Select trade" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
                  <SelectItem value="hvac">HVAC</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="plumbing">Plumbing</SelectItem>
                  <SelectItem value="roofing">Roofing</SelectItem>
                  <SelectItem value="general">General Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            onClick={handleCreateJob}
            disabled={!isActingAsThisOrg || !writeActionsEnabled || !jobForm.title}
            className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Job
          </Button>

          {isActingAsThisOrg && !writeActionsEnabled && (
            <p className="text-xs text-amber-400 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Enable write actions in the banner to create jobs
            </p>
          )}
        </div>
      </AdminCard>

      {/* Run Compliance Check */}
      <AdminCard>
        <div className="flex items-center gap-2 mb-4">
          <Play className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-white">Run Compliance Check</h3>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          Run a compliance check on an existing job to test the analysis pipeline.
        </p>

        <Button
          onClick={handleRunCheck}
          disabled={!isActingAsThisOrg || !canRunChecks}
          variant="outline"
          className="border-[hsl(220,15%,25%)] text-gray-300 hover:bg-[hsl(220,15%,15%)] hover:text-white"
        >
          <Play className="h-4 w-4 mr-2" />
          Select Job & Run Check
        </Button>
      </AdminCard>

      {/* Download Debug Bundle */}
      <AdminCard>
        <div className="flex items-center gap-2 mb-4">
          <Download className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-medium text-white">Debug Bundle</h3>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          Download a JSON export of inputs, outputs, and traces for a compliance run.
        </p>

        <Button
          variant="outline"
          disabled={!isActingAsThisOrg}
          className="border-[hsl(220,15%,25%)] text-gray-300 hover:bg-[hsl(220,15%,15%)] hover:text-white"
        >
          <Download className="h-4 w-4 mr-2" />
          Select Run & Download
        </Button>
      </AdminCard>
    </div>
  );
}
